import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import {
  UserCircle,
  Phone,
  IdentificationCard,
  MapPin,
  Buildings,
  Briefcase,
  WhatsappLogo,
  CheckCircle,
  FloppyDisk,
} from '@phosphor-icons/react';

const API = '/api';

const SPECIALTIES = [
  'Minha Casa Minha Vida',
  'Médio padrão',
  'Alto padrão',
  'Investidores',
  'Imóveis comerciais',
  'Lançamentos',
  'Prontos para morar',
  'Financiamento imobiliário',
];

const STATUS_OPTIONS = [
  { value: 'available', label: 'Disponível' },
  { value: 'busy', label: 'Ocupado' },
  { value: 'away', label: 'Ausente' },
  { value: 'vacation', label: 'Férias' },
  { value: 'offline', label: 'Offline' },
];

const EMPTY_FORM = {
  name: '',
  phone: '',
  whatsapp: '',
  creci: '',
  city: '',
  regions: [],
  specialties: [],
  enterprise_ids: [],
  bio: '',
  photo_url: '',
  operational_status: 'available',
  accepts_auto_distribution: true,
  accepts_whatsapp_briefing: true,
};

const splitCommaValues = (value) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const completionClass = (value) => {
  if (value >= 100) return 'bg-emerald-500';
  if (value >= 70) return 'bg-blue-500';
  return 'bg-amber-500';
};

const MeuPerfilCorretor = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [profile, setProfile] = useState(null);
  const [enterprises, setEnterprises] = useState([]);
  const [regionsText, setRegionsText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);

    try {
      const [profileResponse, enterpriseResponse] = await Promise.all([
        axios.get(`${API}/broker-profile/me`, { withCredentials: true }),
        axios.get(`${API}/enterprises`, { withCredentials: true }),
      ]);

      const data = profileResponse.data;

      setProfile(data);
      setForm({
        ...EMPTY_FORM,
        ...data,
        regions: data.regions || [],
        specialties: data.specialties || [],
        enterprise_ids: data.enterprise_ids || [],
      });

      setRegionsText((data.regions || []).join(', '));
      setEnterprises(enterpriseResponse.data || []);
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.detail || 'Erro ao carregar seu perfil'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleArrayValue = (field, value) => {
    setForm((current) => {
      const values = current[field] || [];
      const exists = values.includes(value);

      return {
        ...current,
        [field]: exists
          ? values.filter((item) => item !== value)
          : [...values, value],
      };
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...form,
        regions: splitCommaValues(regionsText),
      };

      const { data } = await axios.patch(
        `${API}/broker-profile/me`,
        payload,
        { withCredentials: true }
      );

      setProfile(data);
      setForm({
        ...EMPTY_FORM,
        ...data,
        regions: data.regions || [],
        specialties: data.specialties || [],
        enterprise_ids: data.enterprise_ids || [],
      });

      setRegionsText((data.regions || []).join(', '));
      toast.success('Perfil atualizado com sucesso');
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.detail || 'Erro ao salvar o perfil'
      );
    } finally {
      setSaving(false);
    }
  };

  const completion = profile?.profile_completion || 0;

  const completionLabel = useMemo(() => {
    if (completion >= 100) return 'Perfil completo';
    if (completion >= 90) return 'Perfil avançado';
    if (completion >= 70) return 'Perfil operacional';
    return 'Perfil incompleto';
  }, [completion]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="w-14 h-14 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <form onSubmit={handleSave} className="space-y-6 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-indigo-100 flex items-center justify-center">
              <UserCircle size={34} weight="duotone" className="text-indigo-600" />
            </div>

            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                Meu Perfil Profissional
              </h1>
              <p className="text-slate-600 mt-1">
                Seus dados serão utilizados pelo Orkestry Intelligence e no envio de briefings.
              </p>
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-60"
          >
            <FloppyDisk size={20} />
            {saving ? 'Salvando...' : 'Salvar perfil'}
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-sm text-slate-500">Preenchimento do perfil</p>
              <p className="text-2xl font-bold text-slate-900">
                {completion}% — {completionLabel}
              </p>
            </div>

            <div className="w-full md:w-80">
              <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full transition-all ${completionClass(completion)}`}
                  style={{ width: `${Math.min(100, completion)}%` }}
                />
              </div>
            </div>
          </div>

          {completion < 70 && (
            <p className="mt-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Complete pelo menos 70% do perfil para estar plenamente habilitado à distribuição automática.
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <section className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
            <div className="flex items-center gap-2">
              <IdentificationCard size={22} className="text-indigo-600" />
              <h2 className="text-lg font-bold text-slate-900">
                Dados profissionais
              </h2>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Nome completo
              </label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-xl border border-slate-300 px-4 py-3"
                placeholder="Nome completo"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Telefone
                </label>
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3"
                  placeholder="+55 77 99999-9999"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  WhatsApp para briefings
                </label>
                <input
                  value={form.whatsapp}
                  onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3"
                  placeholder="+55 77 99999-9999"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                CRECI
              </label>
              <input
                value={form.creci}
                onChange={(e) => setForm({ ...form, creci: e.target.value })}
                className="w-full rounded-xl border border-slate-300 px-4 py-3"
                placeholder="CRECI 00000-F"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Apresentação profissional
              </label>
              <textarea
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                rows={5}
                className="w-full rounded-xl border border-slate-300 px-4 py-3"
                placeholder="Conte sua experiência, segmentos e diferenciais..."
              />
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
            <div className="flex items-center gap-2">
              <MapPin size={22} className="text-indigo-600" />
              <h2 className="text-lg font-bold text-slate-900">
                Área de atuação
              </h2>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Cidade principal
              </label>
              <input
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="w-full rounded-xl border border-slate-300 px-4 py-3"
                placeholder="Petrolina"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Regiões e bairros
              </label>
              <input
                value={regionsText}
                onChange={(e) => setRegionsText(e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-4 py-3"
                placeholder="Petrolina, Juazeiro, Centro, Zona Leste"
              />
              <p className="text-xs text-slate-500 mt-2">
                Separe as regiões por vírgulas.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">
                Status operacional
              </label>
              <select
                value={form.operational_status}
                onChange={(e) =>
                  setForm({ ...form, operational_status: e.target.value })
                }
                className="w-full rounded-xl border border-slate-300 px-4 py-3"
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <label className="flex items-center justify-between gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
              <div>
                <p className="font-semibold text-slate-900">
                  Distribuição automática
                </p>
                <p className="text-sm text-slate-500">
                  Permitir que o DMB Core envie novos leads.
                </p>
              </div>
              <input
                type="checkbox"
                checked={form.accepts_auto_distribution}
                onChange={(e) =>
                  setForm({
                    ...form,
                    accepts_auto_distribution: e.target.checked,
                  })
                }
                className="w-5 h-5"
              />
            </label>

            <label className="flex items-center justify-between gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
              <div>
                <p className="font-semibold text-slate-900">
                  Briefing no WhatsApp
                </p>
                <p className="text-sm text-slate-500">
                  Receber automaticamente os dados dos leads qualificados.
                </p>
              </div>
              <input
                type="checkbox"
                checked={form.accepts_whatsapp_briefing}
                onChange={(e) =>
                  setForm({
                    ...form,
                    accepts_whatsapp_briefing: e.target.checked,
                  })
                }
                className="w-5 h-5"
              />
            </label>
          </section>
        </div>

        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-5">
            <Briefcase size={22} className="text-indigo-600" />
            <h2 className="text-lg font-bold text-slate-900">
              Especialidades
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {SPECIALTIES.map((specialty) => {
              const selected = form.specialties.includes(specialty);

              return (
                <button
                  type="button"
                  key={specialty}
                  onClick={() => toggleArrayValue('specialties', specialty)}
                  className={`text-left p-4 rounded-xl border transition ${
                    selected
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {selected && <CheckCircle size={18} weight="fill" />}
                    <span className="text-sm font-medium">{specialty}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-5">
            <Buildings size={22} className="text-indigo-600" />
            <h2 className="text-lg font-bold text-slate-900">
              Empreendimentos habilitados
            </h2>
          </div>

          {enterprises.length === 0 ? (
            <p className="text-slate-500">
              Nenhum empreendimento cadastrado.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {enterprises.map((enterprise) => {
                const selected = form.enterprise_ids.includes(enterprise.id);

                return (
                  <button
                    type="button"
                    key={enterprise.id}
                    onClick={() =>
                      toggleArrayValue('enterprise_ids', enterprise.id)
                    }
                    className={`text-left p-4 rounded-xl border ${
                      selected
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-200'
                    }`}
                  >
                    <p className="font-semibold text-slate-900">
                      {enterprise.name}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      {enterprise.city || 'Cidade não informada'}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-60"
          >
            <WhatsappLogo size={21} />
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </button>
        </div>
      </form>
    </Layout>
  );
};

export default MeuPerfilCorretor;
